<script setup>
// "Novo modelo": galeria global por segmento/objetivo, com busca, miniaturas
// e prévia grande (HTML do servidor num iframe sem scripts); modelos Pessoais
// (texto); começar em branco e importar HTML.
import { ref, computed, watch, nextTick, onBeforeUnmount } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import API from 'dashboard/api/marketingEmailTemplates';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';

const emit = defineEmits(['create', 'import']);
const { t } = useI18n();
const SEARCH_DELAY = 300;
const SECTION_LIMIT = 4;
const dialog = ref(null);
const mode = ref('newsletter');
const busy = ref(false);
const loading = ref(true);
const loadFailed = ref(false);
const templates = ref([]);
const personal = ref([]);
const segments = ref([]);
const goals = ref([]);
const segment = ref('');
const goal = ref('');
const query = ref('');
const previewing = ref(null);
const filtered = computed(
  () => !!(segment.value || goal.value || query.value.trim())
);
const sections = computed(() =>
  goals.value
    .map(g => {
      const items = templates.value.filter(tpl => tpl.goal === g);
      return {
        goal: g,
        total: items.length,
        items: items.slice(0, SECTION_LIMIT),
      };
    })
    .filter(sec => sec.total)
);
const previewHtml = ref('');
const previewLoading = ref(false);

let requestId = 0;
const load = async () => {
  requestId += 1;
  const mine = requestId;
  loading.value = true;
  loadFailed.value = false;
  try {
    const { data } = await API.gallery({
      segment: segment.value || undefined,
      goal: goal.value || undefined,
      q: query.value.trim() || undefined,
    });
    if (mine !== requestId) return;
    templates.value = data.templates || [];
    personal.value = data.personal || [];
    segments.value = data.segments || [];
    goals.value = data.goals || [];
  } catch (error) {
    if (mine === requestId) {
      loadFailed.value = true;
      useAlert(t('EMAIL_TEMPLATES.GALLERY.LOAD_ERROR'));
    }
  } finally {
    if (mine === requestId) loading.value = false;
  }
};
let searchTimer = null;
let resetting = false;
watch(query, () => {
  if (resetting) return;
  clearTimeout(searchTimer);
  searchTimer = setTimeout(load, SEARCH_DELAY);
});
watch([segment, goal], () => {
  if (!resetting) load();
});
onBeforeUnmount(() => clearTimeout(searchTimer));

let previewToken = 0;
const openPreview = async tpl => {
  previewToken += 1;
  const mine = previewToken;
  previewing.value = tpl;
  previewHtml.value = '';
  previewLoading.value = true;
  try {
    const { data } = await API.galleryPreview(tpl.id);
    if (mine === previewToken) previewHtml.value = data.html;
  } catch (error) {
    if (mine === previewToken) {
      useAlert(t('EMAIL_TEMPLATES.GALLERY.PREVIEW_ERROR'));
    }
  } finally {
    if (mine === previewToken) previewLoading.value = false;
  }
};
const closePreview = () => {
  previewToken += 1;
  previewing.value = null;
};

const run = async request => {
  if (busy.value) return;
  busy.value = true;
  try {
    const { data } = await request();
    dialog.value?.close();
    closePreview();
    emit('create', data);
  } catch (error) {
    useAlert(error?.response?.data?.error || t('EMAIL_TEMPLATES.SAVE_ERROR'));
  } finally {
    busy.value = false;
  }
};
const useTemplate = tpl => run(() => API.createFromGallery(tpl.id));
const usePreset = preset => run(() => API.createFromPreset(preset.key));
const startBlank = () =>
  run(() =>
    API.create({
      marketing_email_template: {
        mode: mode.value,
        name: t('EMAIL_TEMPLATES.GALLERY.BLANK_NAME'),
      },
    })
  );

const open = () => {
  closePreview();
  clearTimeout(searchTimer);
  resetting = true;
  mode.value = 'newsletter';
  segment.value = '';
  goal.value = '';
  query.value = '';
  nextTick(() => {
    resetting = false;
  });
  load();
  dialog.value?.open();
};
defineExpose({ open });
const chip = active =>
  active
    ? 'border-n-brand bg-n-brand/10 text-n-brand'
    : 'border-n-weak text-n-slate-11 hover:border-n-brand';
</script>

<template>
  <Dialog
    ref="dialog"
    :title="t('EMAIL_TEMPLATES.GALLERY.TITLE')"
    :show-confirm-button="false"
    :show-cancel-button="false"
    full-screen
    overflow-y-auto
  >
    <div class="flex flex-wrap items-center gap-2">
      <button
        type="button"
        data-test="close"
        class="flex size-8 items-center justify-center rounded-lg text-n-slate-11 hover:bg-n-solid-3"
        :aria-label="t('EMAIL_TEMPLATES.GALLERY.CLOSE')"
        @click="dialog?.close()"
      >
        <span class="i-lucide-x" />
      </button>
      <div class="flex items-center gap-1 rounded-lg bg-n-solid-2 p-1">
        <button
          v-for="m in ['newsletter', 'personal']"
          :key="m"
          type="button"
          :data-mode="m"
          class="rounded-md px-3 py-1 text-xs font-medium"
          :class="
            mode === m
              ? 'bg-n-solid-1 text-n-slate-12 shadow'
              : 'text-n-slate-11'
          "
          @click="
            mode = m;
            closePreview();
          "
        >
          {{ t(`EMAIL_TEMPLATES.BUILDER.MODE.${m}`) }}
        </button>
      </div>
      <button
        type="button"
        class="flex items-center gap-1.5 rounded-lg border border-n-weak px-3 py-1.5 text-xs font-medium text-n-slate-12 hover:border-n-brand hover:text-n-brand"
        @click="
          dialog?.close();
          emit('import');
        "
      >
        <span class="i-lucide-file-code-2" />
        {{ t('EMAIL_TEMPLATES.EDITOR.IMPORT.BUTTON') }}
      </button>
    </div>

    <template v-if="previewing">
      <div class="flex flex-wrap items-center gap-3">
        <button
          type="button"
          data-test="back"
          class="flex shrink-0 items-center gap-1 text-sm text-n-slate-11 hover:text-n-slate-12"
          @click="closePreview"
        >
          <span class="i-lucide-arrow-left" />
          {{ t('EMAIL_TEMPLATES.GALLERY.BACK') }}
        </button>
        <div class="min-w-[10rem] flex-1">
          <p class="truncate text-sm font-semibold text-n-slate-12">
            {{ previewing.name }}
          </p>
          <p class="truncate text-xs text-n-slate-11">
            {{ previewing.subject }}
          </p>
        </div>
        <button
          type="button"
          data-test="use"
          :disabled="busy || previewLoading || !previewHtml"
          class="shrink-0 rounded-lg bg-n-brand px-4 py-2 text-sm font-medium text-white disabled:opacity-50"
          @click="useTemplate(previewing)"
        >
          {{ t('EMAIL_TEMPLATES.GALLERY.USE') }}
        </button>
      </div>
      <div
        data-test="preview-box"
        class="h-[calc(100vh-14rem)] min-h-[24rem] w-full shrink-0 overflow-hidden rounded-xl border border-n-weak bg-n-solid-2"
      >
        <p
          v-if="previewLoading"
          class="flex h-full items-center justify-center text-sm text-n-slate-11"
        >
          {{ t('EMAIL_TEMPLATES.LOADING') }}
        </p>
        <iframe
          v-else
          data-test="preview"
          sandbox=""
          class="size-full border-0 bg-white"
          :title="previewing.name"
          :srcdoc="previewHtml"
        />
      </div>
    </template>

    <template v-else-if="mode === 'newsletter'">
      <div class="flex flex-wrap items-center gap-2">
        <button
          type="button"
          data-segment=""
          class="rounded-full border px-3 py-1 text-xs font-medium"
          :class="chip(!segment)"
          @click="segment = ''"
        >
          {{ t('EMAIL_TEMPLATES.GALLERY.ALL_SEGMENTS') }}
        </button>
        <button
          v-for="s in segments"
          :key="s"
          type="button"
          :data-segment="s"
          class="rounded-full border px-3 py-1 text-xs font-medium"
          :class="chip(segment === s)"
          @click="segment = s"
        >
          {{ t(`EMAIL_TEMPLATES.GALLERY.SEGMENTS.${s}`) }}
        </button>
      </div>
      <div class="flex flex-wrap items-center gap-2">
        <select
          v-model="goal"
          data-test="goal"
          class="rounded-lg border border-n-weak bg-n-solid-1 px-2.5 py-1.5 text-sm text-n-slate-12"
        >
          <option value="">{{ t('EMAIL_TEMPLATES.GALLERY.ALL_GOALS') }}</option>
          <option v-for="g in goals" :key="g" :value="g">
            {{ t(`EMAIL_TEMPLATES.GALLERY.GOALS.${g}`) }}
          </option>
        </select>
        <input
          v-model="query"
          data-test="search"
          type="search"
          class="min-w-0 flex-1 rounded-lg border border-n-weak bg-n-solid-1 px-2.5 py-1.5 text-sm text-n-slate-12"
          :placeholder="t('EMAIL_TEMPLATES.GALLERY.SEARCH')"
        />
      </div>
      <div
        v-if="filtered"
        class="grid grid-cols-2 gap-3 md:grid-cols-4 xl:grid-cols-6"
        :class="{ 'opacity-50': loading }"
        :aria-busy="loading"
      >
        <button
          type="button"
          :disabled="busy"
          class="flex aspect-[3/4] flex-col items-center justify-center gap-2 rounded-xl border border-dashed border-n-weak p-3 text-sm text-n-slate-11 hover:border-n-brand hover:text-n-brand disabled:opacity-50"
          @click="startBlank"
        >
          <span class="i-lucide-plus text-xl" />
          {{ t('EMAIL_TEMPLATES.GALLERY.BLANK') }}
        </button>
        <button
          v-for="n in loading && !templates.length ? 6 : 0"
          :key="`sk-${n}`"
          data-test="skeleton"
          class="aspect-[3/4] animate-pulse rounded-xl bg-n-solid-3"
        />
        <button
          v-for="tpl in templates"
          :key="tpl.id"
          type="button"
          :data-gallery="tpl.key"
          class="group flex flex-col gap-2 rounded-xl border border-n-weak bg-n-solid-1 p-2 text-left hover:border-n-brand hover:shadow-md"
          @click="openPreview(tpl)"
        >
          <span
            class="block aspect-[3/4] w-full overflow-hidden rounded-lg border border-n-weak bg-n-solid-3"
          >
            <img
              v-if="tpl.thumbnail_url"
              :src="tpl.thumbnail_url"
              :alt="tpl.name"
              loading="lazy"
              class="h-full w-full object-cover object-top transition-transform duration-300 group-hover:scale-105"
            />
            <span
              v-else
              data-test="no-thumb"
              class="flex size-full flex-col items-center justify-center gap-2 p-3 text-center text-n-slate-10"
            >
              <span class="i-lucide-mail size-8" />
              <span class="text-xs font-medium">{{ tpl.name }}</span>
            </span>
          </span>
          <span class="truncate text-sm font-semibold text-n-slate-12">{{
            tpl.name
          }}</span>
          <span class="truncate text-[11px] text-n-slate-10">
            {{ t(`EMAIL_TEMPLATES.GALLERY.SEGMENTS.${tpl.segment}`) }} ·
            {{ t(`EMAIL_TEMPLATES.GALLERY.GOALS.${tpl.goal}`) }}
          </span>
        </button>
      </div>
      <div
        v-else
        data-test="sections"
        class="flex flex-col gap-6"
        :class="{ 'opacity-50': loading }"
        :aria-busy="loading"
      >
        <div class="flex">
          <button
            type="button"
            data-test="blank"
            :disabled="busy"
            class="flex items-center gap-2 rounded-xl border border-dashed border-n-weak px-4 py-2 text-sm text-n-slate-11 hover:border-n-brand hover:text-n-brand disabled:opacity-50"
            @click="startBlank"
          >
            <span class="i-lucide-plus" />
            {{ t('EMAIL_TEMPLATES.GALLERY.BLANK') }}
          </button>
        </div>
        <div
          v-if="loading && !templates.length"
          class="grid grid-cols-2 gap-3 md:grid-cols-4"
        >
          <button
            v-for="n in loading && !templates.length ? 6 : 0"
            :key="`sk-${n}`"
            data-test="skeleton"
            class="aspect-[3/4] animate-pulse rounded-xl bg-n-solid-3"
          />
        </div>
        <section
          v-for="sec in sections"
          :key="sec.goal"
          :data-section="sec.goal"
          class="flex flex-col gap-3"
        >
          <div class="flex items-center justify-between gap-2">
            <h3 class="text-sm font-semibold text-n-slate-12">
              {{ t(`EMAIL_TEMPLATES.GALLERY.GOALS.${sec.goal}`) }}
            </h3>
            <button
              v-if="sec.total > sec.items.length"
              type="button"
              data-test="see-all"
              class="text-xs font-medium text-n-brand hover:underline"
              @click="goal = sec.goal"
            >
              {{ t('EMAIL_TEMPLATES.GALLERY.SEE_ALL', { count: sec.total }) }}
            </button>
          </div>
          <div class="grid grid-cols-2 gap-3 md:grid-cols-4">
            <button
              v-for="tpl in sec.items"
              :key="tpl.id"
              type="button"
              :data-gallery="tpl.key"
              class="group flex flex-col gap-2 rounded-xl border border-n-weak bg-n-solid-1 p-2 text-left hover:border-n-brand hover:shadow-md"
              @click="openPreview(tpl)"
            >
              <span
                class="block aspect-[3/4] w-full overflow-hidden rounded-lg border border-n-weak bg-n-solid-3"
              >
                <img
                  v-if="tpl.thumbnail_url"
                  :src="tpl.thumbnail_url"
                  :alt="tpl.name"
                  loading="lazy"
                  class="h-full w-full object-cover object-top transition-transform duration-300 group-hover:scale-105"
                />
                <span
                  v-else
                  data-test="no-thumb"
                  class="flex size-full flex-col items-center justify-center gap-2 p-3 text-center text-n-slate-10"
                >
                  <span class="i-lucide-mail size-8" />
                  <span class="text-xs font-medium">{{ tpl.name }}</span>
                </span>
              </span>
              <span class="truncate text-sm font-semibold text-n-slate-12">{{
                tpl.name
              }}</span>
              <span class="truncate text-[11px] text-n-slate-10">
                {{ t(`EMAIL_TEMPLATES.GALLERY.SEGMENTS.${tpl.segment}`) }} ·
                {{ t(`EMAIL_TEMPLATES.GALLERY.GOALS.${tpl.goal}`) }}
              </span>
            </button>
          </div>
        </section>
      </div>
      <p
        v-if="!loading && !loadFailed && !templates.length"
        class="text-center text-sm text-n-slate-11"
      >
        {{ t('EMAIL_TEMPLATES.GALLERY.EMPTY') }}
      </p>
    </template>

    <template v-else>
      <p class="text-xs text-n-slate-11">
        {{ t('EMAIL_TEMPLATES.GALLERY.PERSONAL_HINT') }}
      </p>
      <div class="grid grid-cols-2 gap-3 md:grid-cols-3">
        <button
          v-for="p in personal"
          :key="p.key"
          type="button"
          :data-preset="p.key"
          :disabled="busy"
          class="flex flex-col gap-2 rounded-xl border border-n-weak bg-n-solid-1 p-3 text-left hover:border-n-brand disabled:opacity-50"
          @click="usePreset(p)"
        >
          <span class="text-sm font-semibold text-n-slate-12">{{
            p.name
          }}</span>
          <span class="text-xs leading-snug text-n-slate-11">{{
            p.description
          }}</span>
        </button>
        <button
          type="button"
          :disabled="busy"
          class="flex flex-col items-center justify-center gap-2 rounded-xl border border-dashed border-n-weak p-3 text-sm text-n-slate-11 hover:border-n-brand hover:text-n-brand disabled:opacity-50"
          @click="startBlank"
        >
          <span class="i-lucide-plus text-xl" />
          {{ t('EMAIL_TEMPLATES.GALLERY.BLANK') }}
        </button>
      </div>
    </template>
  </Dialog>
</template>
